import {useMutation} from '@github-ui/react-query'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataContext} from '@github-ui/pull-request-page-data-tooling/page-data-context'
import {
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'

/**
 * Removes all pull requests from a stack and destroys the stack.
 *
 * To run additional callbacks, you can pass any supported callback option to the `mutate` function when it's
 * called, including `onSuccess`, `onError`, and `onSettled`. These additional callbacks will run after the
 * callbacks defined in the hook below.
 *
 * For more details, see https://tanstack.com/query/latest/docs/framework/react/guides/mutations#mutation-side-effects
 *
 * @example
 * ```ts
 * const {mutate} = useUnstackPullRequestsMutation()
 *
 * // Required arguments
 * const mutationArguments = {stackId: 123}
 *
 * // Optional callbacks will run after callbacks defined in useUnstackPullRequestsMutation hook
 * const mutationCallbacks = {
 *   onError: error => console.log(error.message),
 *   onSuccess: (data) => console.log(data),
 * }
 *
 * mutate(mutationArguments, mutationCallbacks)
 * ```
 */
export function useUnstackPullRequestsMutation() {
  // TODO update to use `usePullRequestBasePath` when all callsites have access to pull request store
  const {basePageDataUrl} = usePageDataContext()
  const basePath = `${basePageDataUrl}/page_data/${PageData.unstackPullRequests}`

  return useMutation({
    mutationFn: async ({stackId}: {stackId: number}) => {
      const unstackPullRequestsUrl = `${basePath}/${stackId}`
      const response = await fetchWithErrorHandling(unstackPullRequestsUrl, {
        method: 'DELETE',
      })
      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
      return json
    },
  })
}
